/**
 * Notification Panel Component
 *
 * Dropdown panel showing recent notifications with actions.
 * Combines notifications from:
 * 1. Main notification store (route calculations, sync events, HMC submissions)
 * 2. Dashboard notification store (PUG lead alerts)
 *
 * Displays last 10 notifications with "View All" link to full page.
 */

import React, { useEffect, useRef, useMemo, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  MapPin,
  Route,
  RefreshCw,
  X,
  ExternalLink,
  FileText,
  CalendarDays,
  Upload,
  Image,
  Ticket,
} from 'lucide-react';
import { useRecentNotifications, useNotificationActions } from '@shared/store/notificationStore';
import {
  useDashboardNotificationStore,
  useDashboardNotificationActions,
  type DashboardNotification,
} from '@pages/dashboard/store/dashboardNotificationStore';
import type { Notification, NotificationSeverity } from '@shared/types/notifications';
import { formatDistanceToNow } from 'date-fns';

interface NotificationPanelProps {
  onClose: () => void;
  /** Reference to the button that triggers this panel - used for positioning via portal */
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * Get icon for notification severity
 */
function getSeverityIcon(severity: NotificationSeverity) {
  switch (severity) {
    case 'success':
      return <CheckCircle className="h-5 w-5 text-green-400" />;
    case 'error':
      return <AlertCircle className="h-5 w-5 text-red-400" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
    case 'info':
    default:
      return <Info className="h-5 w-5 text-blue-400" />;
  }
}

/**
 * Get icon for notification type
 */
function getTypeIcon(type: string) {
  if (type === 'dashboard_pug_leads') return <Upload className="h-4 w-4 text-green-400" />;
  if (type === 'postcard_uploaded') return <Image className="h-4 w-4 text-amber-400" />;
  if (type.startsWith('hmc_')) return <Ticket className="h-4 w-4 text-purple-400" />;
  if (type.includes('geocoding')) return <MapPin className="h-4 w-4" />;
  if (type.includes('route')) return <Route className="h-4 w-4" />;
  if (type.includes('pug')) return <FileText className="h-4 w-4 text-purple-400" />;
  if (type.includes('hmc')) return <CalendarDays className="h-4 w-4 text-orange-400" />;
  if (type.includes('sync')) return <RefreshCw className="h-4 w-4" />;
  return <Info className="h-4 w-4" />;
}

/**
 * Unified notification type that works with both stores
 */
interface UnifiedNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  severity: NotificationSeverity;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  source: 'main' | 'dashboard';
}

/**
 * Notification Item Component
 */
const NotificationItem: React.FC<{
  notification: UnifiedNotification;
  onClose: () => void;
  onMarkAsRead: (id: string, source: 'main' | 'dashboard') => void;
  onDelete: (id: string, source: 'main' | 'dashboard') => void;
}> = ({ notification, onClose, onMarkAsRead, onDelete }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    onMarkAsRead(notification.id, notification.source);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id, notification.source);
  };

  const timeAgo = formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true });

  return (
    <div
      className={`
        group relative p-3 border-b border-dark-border/30
        ${notification.read ? 'bg-dark-surface' : 'bg-dark-surface/50'}
        ${notification.actionUrl ? 'cursor-pointer hover:bg-dark-surface/80' : ''}
        transition-all duration-150
      `}
      onClick={handleClick}
    >
      {/* Unread Indicator */}
      {!notification.read && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500" />
      )}
      <div className="flex gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getSeverityIcon(notification.severity)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              {getTypeIcon(notification.type)}
              <h4 className="text-sm font-medium text-dark-text">
                {notification.title}
              </h4>
            </div>

            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className="
                opacity-0 group-hover:opacity-100
                p-1 rounded hover:bg-dark-bg/50
                text-dark-muted hover:text-dark-text
                transition-all
              "
              aria-label="Delete notification"
              data-interaction-id='button-delete-notification'>
              <X className="h-3 w-3" />
            </button>
          </div>

          <p className="text-xs text-dark-muted mt-1 line-clamp-2">
            {notification.message}
          </p>

          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-dark-muted/70">{timeAgo}</span>

            {notification.actionUrl && (
              <span className="text-xs text-brand-400 flex items-center gap-1">
                {notification.actionLabel || 'View'}
                <ExternalLink className="h-3 w-3" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Notification Panel Component
 *
 * Combines notifications from main store and dashboard store,
 * sorted by timestamp (newest first).
 *
 * Uses a portal to render outside the overflow-hidden container,
 * ensuring the dropdown is always visible.
 */
export const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose, buttonRef }) => {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  // Main notification store
  const mainNotifications = useRecentNotifications(10);
  const mainActions = useNotificationActions();

  // Dashboard notification store (PUG alerts, HMC submissions)
  const dashboardNotifications = useDashboardNotificationStore((state) => state.notifications);
  const dashboardActions = useDashboardNotificationActions();

  // Combine and sort notifications from both sources
  const combinedNotifications = useMemo((): UnifiedNotification[] => {
    // Convert main notifications
    const mainWithSource: UnifiedNotification[] = mainNotifications.map((n) => ({
      id: n.id,
      type: n.type,
      title: n.title,
      message: n.message,
      severity: n.severity,
      timestamp: n.timestamp,
      read: n.read,
      actionUrl: n.actionUrl,
      actionLabel: n.actionLabel,
      source: 'main' as const,
    }));

    // Convert dashboard notifications
    const dashboardWithSource: UnifiedNotification[] = dashboardNotifications.map((n) => ({
      id: n.id,
      type: n.type,
      title: n.title,
      message: n.message,
      severity: n.severity as NotificationSeverity,
      timestamp: n.timestamp,
      read: n.read,
      actionUrl: n.actionUrl,
      source: 'dashboard' as const,
    }));

    // Combine and sort by timestamp (newest first), take top 10
    return [...mainWithSource, ...dashboardWithSource]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);
  }, [mainNotifications, dashboardNotifications]);

  // Check if there are any unread notifications
  const hasUnread = combinedNotifications.some((n) => !n.read);

  // Handle mark as read based on source
  const handleMarkAsRead = (id: string, source: 'main' | 'dashboard') => {
    if (source === 'main') {
      mainActions.markAsRead(id);
    } else {
      dashboardActions.markAsRead(id);
    }
  };

  // Handle delete based on source
  const handleDelete = (id: string, source: 'main' | 'dashboard') => {
    if (source === 'main') {
      mainActions.deleteNotification(id);
    } else {
      dashboardActions.deleteNotification(id);
    }
  };

  // Handle mark all as read in both stores
  const handleMarkAllRead = () => {
    mainActions.markAllAsRead();
    dashboardActions.markAllAsRead();
  };

  // Calculate position based on button ref
  useLayoutEffect(() => {
    if (buttonRef?.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: window.innerWidth - rect.right,
      });
    }
  }, [buttonRef]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside both the panel and the button
      const isOutsidePanel = panelRef.current && !panelRef.current.contains(event.target as Node);
      const isOutsideButton = buttonRef?.current && !buttonRef.current.contains(event.target as Node);

      if (isOutsidePanel && isOutsideButton) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, buttonRef]);

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  // Use portal to render outside overflow-hidden containers
  const panelContent = (
    <div
      ref={panelRef}
      style={position ? {
        position: 'fixed',
        top: position.top,
        right: position.right,
      } : undefined}
      className={`
        ${!position ? 'absolute right-0 top-full mt-2' : ''}
        w-[380px] max-w-[calc(100vw-2rem)]
        bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl
        shadow-2xl shadow-black/50
        overflow-hidden
        z-[9999]
        animate-in fade-in slide-in-from-top-2
      `}
    >
      {/* Header */}
      <div className="p-4 border-b border-dark-border/30 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Notifications</h3>
        {hasUnread && (
          <button
            onClick={handleMarkAllRead}
            className="text-xs text-brand-400 hover:text-brand-300 transition-all duration-150"
            data-interaction-id='button-mark-all-read'>
            Mark all read
          </button>
        )}
      </div>
      {/* Notification List */}
      <div className="max-h-[400px] overflow-y-auto">
        {combinedNotifications.length === 0 ? (
          <div className="p-8 text-center">
            <Info className="h-12 w-12 text-dark-border mx-auto mb-3 opacity-50" />
            <p className="text-sm text-dark-muted">No notifications yet</p>
            <p className="text-xs text-dark-muted/70 mt-1">
              PUG alerts, HMC submissions, and system updates will appear here
            </p>
          </div>
        ) : (
          combinedNotifications.map((notification) => (
            <NotificationItem
              key={`${notification.source}-${notification.id}`}
              notification={notification}
              onClose={onClose}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
      {/* Footer */}
      {combinedNotifications.length > 0 && (
        <div className="p-3 border-t border-dark-border/30 bg-dark-bg/50">
          <button
            onClick={handleViewAll}
            className="
              w-full py-2 px-3
              text-sm text-brand-400 hover:text-brand-300
              hover:bg-dark-surface/50
              rounded
              transition-all duration-150
            "
            data-interaction-id='button-view-all-notifications'>
            View all notifications
          </button>
        </div>
      )}
    </div>
  );

  // If buttonRef is provided, use portal for proper positioning outside overflow-hidden containers
  if (buttonRef) {
    return createPortal(panelContent, document.body);
  }

  // Fallback to relative positioning
  return panelContent;
};
